import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { Box } from '@mui/material'

import { assertType } from 'utils'
import ColumnPicker from 'views/CalculateEmissions/RFQ/components/ColumnPicker'
import Field from 'views/CalculateEmissions/RFQ/components/Field'
import UnitPicker from 'views/CalculateEmissions/RFQ/components/UnitPicker'
import ValueMapping from 'views/CalculateEmissions/RFQ/components/ValueMapping'
import {
    getMappedValue,
    ParameterName,
    parseParameterName,
    PropertyMapping,
    RowNumber,
    ValueMappingSource,
} from 'views/CalculateEmissions/RFQ/utils/sharedLogic'
import { AutoFillEntryWithUpdate, RFQTemplateState } from 'views/CalculateEmissions/RFQ/utils/types'
import {
    createDefaultParameterMapping,
    extractDifferentColumnValues,
    labelsMap,
    updateBothAutoFill,
} from 'views/CalculateEmissions/RFQ/utils/utils'

export type CompositeParameterName =
    // These names are composite names, they need to correspond to keys of
    // TransportModeMappingData joined with key of the respective properties inside
    // that type using underscore as the joining character.
    //
    // Need to be kept in sync with ParameterName.
    // TODO improve types here so we have safety and can link actual transport mode
    // properties + transport modes to parameter names here
    | 'Container Ship_refrigeration'
    | 'Container Ship_tradelane'
    | 'Container Ship_vesselImo'
    | 'Container Ship_vesselName'
    | 'Container Ship_departureOn'
    | 'Container Ship_arrivalOn'
    | 'Container Ship_transitCorridor'
    | 'Container Ship_restrictedTransitCorridor'
    | 'Cargo Plane_aircraftType'
    | 'Cargo Plane_flightNumber'
    | 'Cargo Plane_departureOn'
    | 'Passenger Plane_aircraftType'
    | 'Passenger Plane_flightNumber'
    | 'Passenger Plane_departureOn'
    | 'Plane_aircraftType'
    | 'Plane_flightNumber'
    | 'Plane_departureOn'
    | 'Truck_fuel'
    | 'Inferred truck_fuel'
    | 'Truck rigid 7.5t_fuel'
    | 'Truck rigid 12t_fuel'
    | 'Truck rigid 20t_fuel'
    | 'Truck rigid 26t_fuel'
    | 'Truck rigid 32t_fuel'
    | 'Truck articulated 34t_fuel'
    | 'Truck articulated 40t_fuel'
    | 'Truck articulated 44t_fuel'
    | 'Truck articulated 60t_fuel'
    | 'Truck articulated 72t_fuel'
    | 'Truck_refrigeration'
    | 'Inferred truck_refrigeration'
    | 'Truck rigid 7.5t_refrigeration'
    | 'Truck rigid 12t_refrigeration'
    | 'Truck rigid 20t_refrigeration'
    | 'Truck rigid 26t_refrigeration'
    | 'Truck rigid 32t_refrigeration'
    | 'Truck articulated 34t_refrigeration'
    | 'Truck articulated 40t_refrigeration'
    | 'Truck articulated 44t_refrigeration'
    | 'Truck articulated 60t_refrigeration'
    | 'Truck articulated 72t_refrigeration'
    | 'Truck_vehicleType'
    | 'Electric truck_refrigeration'
    | 'Electric van_refrigeration'

export type ExtendedParameterName = ParameterName | CompositeParameterName

/**
 * Given an extended parameter, returns the transport mode and parameter. If the parameter
 * is not composite parameter the function will throw an error
 */
export function decodeTransportModeParameter(parameter: ExtendedParameterName): {
    transportMode: string
    parameter: string
} {
    const parts = parameter.split('_')
    if (parts.length !== 2) {
        throw new Error(`Bad split, got these parts: ${parts}`)
    }
    return {
        transportMode: parts[0],
        parameter: parts[1],
    }
}

export type ParameterMappingProps<T> = {
    state: RFQTemplateState
    parameter: ExtendedParameterName
    mappingType: ValueMappingSource
    outputValues: readonly T[]
    formatOutput: (value: T) => string
    propertyMapping: PropertyMapping<T> | undefined
    onUpdate: (parameterName: ExtendedParameterName, data: PropertyMapping<T> | undefined) => void
    autoFill?: AutoFillEntryWithUpdate
    // TODO unify all these isX properties, they're mutually exclusive, can be just a union type parameter.
    isTradelane?: boolean
    isTransportMode?: boolean
    isCountryCode?: boolean
    error?: string
    useTruncatedLabel?: boolean
    routeLabel?: 'Origin' | 'Destination'
    legNumber?: number
    allowedRowIndexes?: number[]
}

// TODO allow constant value choice, not only column selection
const ParameterMapping = <T,>({
    state,
    parameter,
    mappingType,
    outputValues,
    formatOutput,
    propertyMapping,
    onUpdate,
    isTradelane,
    isTransportMode,
    isCountryCode,
    error,
    useTruncatedLabel,
    autoFill,
    routeLabel,
    legNumber,
    allowedRowIndexes,
}: ParameterMappingProps<T>) => {
    // SAFETY: We depend on ExtendedParameterName and ParameterName to be in sync, in which case
    // all ExtendedParameterName values after discarding prefixes with the underscore should
    // be valid ParameterName values.
    const truncatedParameterName = parseParameterName(parameter.split('_').at(-1)!).unwrap()
    const localLabel = useTruncatedLabel ? labelsMap[truncatedParameterName] : labelsMap[parameter]

    /**
     * Generate the example to display. To note that empty string and `undefined` have different
     * meanings. Empty string means the mapping is set but no example exists (possible empty
     * cell value) while `undefined` means no mapping exists. This changes the behaviour of what
     * is presented to the user (do we show the property being mapped or not)
     */
    function generateExample(mapping: PropertyMapping<T> | undefined): string | undefined {
        if (mapping === undefined) {
            return undefined
        } else if (mapping.source === 'constant') {
            return formatOutput(mapping.value)
        } else {
            assertType<'column'>(mapping.source)
            const rawExampleValue = state.exampleRow[mapping.columnIndex]
            if (mapping.valueMapping.source === 'direct') {
                return rawExampleValue
            } else if (mapping.valueMapping.source === 'unit') {
                return `${rawExampleValue}`
            } else {
                assertType<'mapping'>(mapping.valueMapping.source)
                const mappedExample = getMappedValue(mapping.valueMapping.mappings, rawExampleValue)
                return mappedExample.isSome() ? formatOutput(mappedExample.value) : ''
            }
        }
    }

    const exampleToShow = generateExample(propertyMapping)

    const handleColumnChange = (index: number | undefined) => {
        let data: PropertyMapping<any> | undefined

        updateBothAutoFill(autoFill, false)
        if (index !== undefined) {
            data = createDefaultParameterMapping({
                state,
                columnIndex: index,
                parameter: truncatedParameterName,
                legNumber,
                outputValues,
                mappingType,
                columnValuesExtractor: (columnIndex: number) =>
                    extractDifferentColumnValues(
                        state.sheet,
                        columnIndex,
                        RowNumber.fromZeroBased(state.headerRow),
                        RowNumber.fromZeroBased(state.endRow),
                        allowedRowIndexes,
                    ),
                routeLabel,
            })
            if (data.source === 'column' && data.valueMapping.source !== 'direct') {
                autoFill?.mapping.update(true)
            }
        }

        onUpdate(parameter, data)
    }

    const handleUnitChange = (unit: T) => {
        autoFill?.mapping.update(false)
        onUpdate(parameter, {
            ...propertyMapping,
            valueMapping: { source: 'unit', unit },
        } as PropertyMapping<T>)
    }

    const handleValueMappingChange = (mappings: [string, T][]) => {
        autoFill?.mapping.update(false)
        // If it's a complex mapping we have decided to take it as a confirmation column is correct
        autoFill?.column.update(false)
        onUpdate(parameter, {
            ...propertyMapping,
            valueMapping: { source: 'mapping', mappings },
        } as PropertyMapping<T>)
    }

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                flexDirection: 'row',
                gap: 2,
                mb: 1 / 2,
            }}
        >
            <Box
                sx={{
                    width: '45%',
                }}
            >
                <ColumnPicker
                    {...state}
                    error={error}
                    label={localLabel}
                    columnIndex={
                        propertyMapping?.source === 'column'
                            ? propertyMapping.columnIndex
                            : undefined
                    }
                    mappings={
                        propertyMapping?.source === 'column' &&
                        propertyMapping.valueMapping.source === 'mapping'
                            ? propertyMapping.valueMapping.mappings
                            : undefined
                    }
                    columnAutoFilled={autoFill?.column.value}
                    onUpdate={handleColumnChange}
                    parameterName={truncatedParameterName}
                    fetchColumnValues={(index: number) =>
                        extractDifferentColumnValues(
                            state.sheet,
                            index,
                            RowNumber.fromZeroBased(state.headerRow),
                            RowNumber.fromZeroBased(state.endRow),
                            allowedRowIndexes,
                        )
                    }
                />
            </Box>
            {exampleToShow !== undefined && (
                <>
                    <Box
                        sx={{
                            height: '56px',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <ArrowForwardIcon />
                    </Box>
                    <Box sx={{ width: '45%' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexDirection: 'row',
                                gap: 1 / 2,
                            }}
                        >
                            {/* READ ONLY */}
                            {!(
                                propertyMapping?.source === 'column' &&
                                propertyMapping.valueMapping.source === 'mapping'
                            ) && <Field label={localLabel} />}

                            {/* EDITABLE */}
                            {propertyMapping?.source === 'column' &&
                                propertyMapping.valueMapping.source === 'unit' && (
                                    <UnitPicker
                                        units={outputValues}
                                        formatUnit={formatOutput}
                                        selectedUnit={propertyMapping.valueMapping.unit}
                                        onUpdate={handleUnitChange}
                                        autoFill={autoFill?.mapping}
                                    />
                                )}
                            {propertyMapping?.source === 'column' &&
                                propertyMapping.valueMapping.source === 'mapping' && (
                                    <ValueMapping
                                        label={localLabel}
                                        isTradelane={isTradelane}
                                        isTransportMode={isTransportMode}
                                        isCountryCode={isCountryCode}
                                        outputValues={outputValues}
                                        formatOutput={formatOutput}
                                        mappings={propertyMapping.valueMapping.mappings}
                                        onUpdate={handleValueMappingChange}
                                        autoFill={autoFill?.mapping.value}
                                    />
                                )}
                        </Box>
                    </Box>
                </>
            )}
        </Box>
    )
}

export default ParameterMapping
